import {
    componentClasses,
    componentName,
    dayNames,
    defaultProps,
    eventMap,
    getDateHash,
    getFirstDayOfMonthSlotIndex,
    getFirstSlotDate,
    getInactiveDatesMap,
    getLastDayOfMonthSlotIndex,
    getWeekStartDayIndex,
    isDateOutsideSlots,
    isValidDate,
    schema,
    slotNumber,
    views,
} from "../../../core/components/date-picker/calendar";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { dispatchEvent, renderState } from "../../../core/js/state-machine";
import { createElement } from "../../../core/js/utilities";
import {
    formatDate,
    getLocaleDateString,
    isDateAfter,
    isDateBefore,
    isSameDate,
    isSameDateOrAfter,
    isSameDateOrBefore,
} from "../../../core/js/utils/date";
import { validate as validateProps } from "../../../core/js/validate-props";
import {
    calendarBodyTemplate,
    calendarFooterTemplate,
    calendarHeaderTemplate,
} from "./calendar.template";

/**
 * Creates Calendar
 * @param {HTMLElement} node
 * @returns Calendar
 */
const Calendar = (element, props) => {
    const [success, mergedProps] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        views,
        schema,
    });

    if (!success) return;

    if (isSameDateOrAfter(mergedProps.startDate, mergedProps.endDate, "day")) {
        console.warn("startDate is same or after endDate");
        return false;
    }

    /**
     * Build calendar base structure
     */
    const buildStructure = () => {
        element.insertAdjacentHTML("beforeend", calendarHeaderTemplate());
        element.insertAdjacentHTML("beforeend", calendarBodyTemplate());
        element.insertAdjacentHTML("beforeend", calendarFooterTemplate());
    };

    /**
     * Build calendar week days ordered based on weekStart property
     */
    const buildWeekDays = () => {
        let sortedDayNames = Object.keys(dayNames);

        if (weekStartDayIndex < 0) {
            console.warn(`${componentName} has wrong "weekStart" provided`);
        } else if (weekStartDayIndex > 0) {
            // Copy days left to weekStartDayIndex
            const left = sortedDayNames.slice(0, weekStartDayIndex);
            // Copy weekStartDayIndex and days right to it
            const right = sortedDayNames.slice(weekStartDayIndex, sortedDayNames.length);
            sortedDayNames = right.concat(left);
        }

        const weekStartBase = new Date();
        weekStartBase.setDate(
            weekStartBase.getDate() + (weekStartDayIndex - weekStartBase.getDay())
        );

        gridHeadRow.innerHTML = "";
        const fragment = document.createDocumentFragment();

        sortedDayNames.forEach(() => {
            const dayName = getLocaleDateString(weekStartBase, mergedProps.locale, {
                weekday: "short",
            });

            const weekDayNode = createElement("div");
            weekDayNode.setAttribute("role", "columnheader");
            weekDayNode.setAttribute(
                "abbr",
                getLocaleDateString(weekStartBase, mergedProps.locale, {
                    weekday: "long",
                })
            );
            weekDayNode.classList.add(componentClasses.weekDay);
            weekDayNode.textContent = dayName;

            fragment.appendChild(weekDayNode);

            weekStartBase.setDate(weekStartBase.getDate() + 1);
        });

        gridHeadRow.appendChild(fragment);
    };

    /**
     * Build the day slots
     * @returns {Array<HTMLElement>} - Array of day slots
     */
    const buildDaysSlots = () => {
        const slots = [];

        for (let daySlot = 0; daySlot < slotNumber; daySlot++) {
            const daySlotNode = createElement("div");
            daySlotNode.setAttribute("role", "cell");
            daySlotNode.classList.add(componentClasses.day.base);
            daySlotNode.setAttribute("tabindex", "-1");

            gridBodyRows[Math.floor(daySlot / 7)].appendChild(daySlotNode);

            slots.push({ element: daySlotNode });
        }

        slots.forEach((daySlot) => {
            daySlot.element.addEventListener("click", handleDaySlotClick(daySlot));
            daySlot.element.addEventListener("mousedown", handleDaySlotMousedown);
            daySlot.element.addEventListener("keydown", handleDaySlotKeydown(daySlot));
        });

        return slots;
    };

    /**
     * Update footer button texts
     */
    const updateFooterButtons = () => {
        todayButton.textContent = mergedProps.todayLabel;
        cancelButton.textContent = mergedProps.cancelLabel;
        confirmButton.textContent = mergedProps.confirmLabel;
    };

    /**
     * Build month selector option
     * @param {Date} month
     * @returns {HTMLElement}
     */
    const buildMonthOption = (month) => {
        const option = createElement("option");
        option.value = month.getMonth();
        option.textContent = getLocaleDateString(month, mergedProps.locale, { month: "long" });
        return option;
    };

    /**
     * Update month select options
     */
    const updateMonthSelect = () => {
        monthSelect.innerHTML = "";
        monthSelect.setAttribute("aria-label", mergedProps.monthSrLabel);
        const optionsFragment = document.createDocumentFragment();

        for (let month = 0; month <= 11; month++) {
            const monthDate = new Date(currentDate.getFullYear(), month, 1);
            if (
                isSameDateOrAfter(monthDate, mergedProps.startDate, "month") &&
                isSameDateOrBefore(monthDate, mergedProps.endDate, "month")
            ) {
                optionsFragment.appendChild(buildMonthOption(monthDate));
            }
        }

        monthSelect.appendChild(optionsFragment);
        monthSelect.value = currentDate.getMonth();

        if (isSameDate(mergedProps.startDate, mergedProps.endDate, "month")) {
            monthSelect.disabled = true;
        } else {
            monthSelect.disabled = false;
        }
    };

    /**
     * Update year select options
     */
    const updateYearSelect = () => {
        yearSelect.innerHTML = "";
        yearSelect.setAttribute("aria-label", mergedProps.yearSrLabel);
        const optionsFragment = document.createDocumentFragment();

        for (
            let year = mergedProps.startDate.getFullYear();
            year <= mergedProps.endDate.getFullYear();
            year++
        ) {
            const option = createElement("option");
            option.value = year;
            option.textContent = year;
            optionsFragment.appendChild(option);
        }

        yearSelect.appendChild(optionsFragment);
        yearSelect.value = currentDate.getFullYear();

        if (isSameDate(mergedProps.startDate, mergedProps.endDate, "year")) {
            yearSelect.disabled = true;
        } else {
            yearSelect.disabled = false;
        }
    };

    /**
     * Update month/year polite element with currentDate month and year so it gets read by screen readers
     */
    const updateMonthYearPolite = () => {
        const politeDate = new Date(currentDate);
        headerPolite.textContent = `${getLocaleDateString(politeDate, mergedProps.locale, {
            month: "long",
        })} ${getLocaleDateString(politeDate, mergedProps.locale, {
            year: "numeric",
        })}`;
    };

    /**
     * Update month control buttons (prev/next)
     */
    const updateMonthControlButtons = () => {
        prevButton.setAttribute("aria-label", mergedProps.prevSrLabel);
        nextButton.setAttribute("aria-label", mergedProps.nextSrLabel);

        if (isSameDate(currentDate, mergedProps.startDate, "month")) {
            prevButton.disabled = true;
        } else {
            prevButton.disabled = false;
        }

        if (isSameDate(currentDate, mergedProps.endDate, "month")) {
            nextButton.disabled = true;
        } else {
            nextButton.disabled = false;
        }
    };

    /**
     * Update day slots based on currentDate
     */
    const updateDays = () => {
        const initialSlotDate = getFirstSlotDate(currentDate, weekStartDayIndex);
        const firstDayOfMonthSlotIndex = getFirstDayOfMonthSlotIndex(
            currentDate,
            weekStartDayIndex
        );
        const lastDayOfMonthSlotIndex = getLastDayOfMonthSlotIndex(currentDate, weekStartDayIndex);

        const initialDate = new Date(initialSlotDate);

        daySlots.forEach((slot, slotIndex) => {
            // Clean
            renderState(views.day.base, slot.element);

            slot.date = new Date(initialDate);

            // Add day number
            slot.element.textContent = initialDate.getDate();

            // Start slot dynamic view so all classes and attributes are rendered one time
            const slotView = { classList: [], attrs: {} };

            slotView.attrs["data-date"] = initialDate.getDate();
            slotView.attrs["data-month"] = initialDate.getMonth();
            slotView.attrs["data-full-year"] = initialDate.getFullYear();

            if (slotIndex < firstDayOfMonthSlotIndex || slotIndex > lastDayOfMonthSlotIndex) {
                slotView.classList.push(componentClasses.day.outMonth);
            }

            if (
                isDateAfter(initialDate, mergedProps.endDate) ||
                isDateBefore(initialDate, mergedProps.startDate) ||
                inactiveDates.has(getDateHash(initialDate))
            ) {
                slotView.classList.push(componentClasses.day.inactive);
                slotView.attrs["aria-disabled"] = "true";
            } else {
                slotView.attrs["aria-disabled"] = "false";
            }

            if (isSameDate(initialDate, new Date()))
                slotView.classList.push(componentClasses.day.today);

            if (isSameDate(initialDate, selectedDate)) {
                slotView.classList.push(componentClasses.day.selected);
                slotView.attrs["aria-selected"] = "true";
            } else {
                slotView.attrs["aria-selected"] = "false";
            }

            renderState(slotView, slot.element);

            // Increment initialDate
            initialDate.setDate(initialDate.getDate() + 1);
        });
    };

    /**
     * Centralize all the calendar render updates
     */
    const updateCalendar = () => {
        updateMonthSelect();
        updateYearSelect();
        updateMonthControlButtons();
        updateDays();
        updateFooterButtons();
        updateMonthYearPolite();
    };

    /**
     * Get the available view date
     * @param {Date} date
     * @returns {Date}
     */
    const getAvailableViewDate = (date) => {
        const today = new Date();

        if (
            date &&
            isSameDateOrBefore(date, mergedProps.endDate) &&
            isSameDateOrAfter(date, mergedProps.startDate)
        ) {
            return new Date(date);
        }

        if (
            isSameDateOrBefore(today, mergedProps.endDate) &&
            isSameDateOrAfter(today, mergedProps.startDate)
        ) {
            return today;
        }

        if (isSameDateOrBefore(today, mergedProps.startDate)) {
            return mergedProps.startDate;
        }

        if (isSameDateOrAfter(today, mergedProps.endDate)) {
            return mergedProps.endDate;
        }

        return mergedProps.startDate;
    };

    /**
     * Get all the elements on the calendar structure
     * @returns {Object}
     */
    const getElements = () => {
        return {
            prevButton: element.querySelector(`.${componentClasses.prevButton}`),
            nextButton: element.querySelector(`.${componentClasses.nextButton}`),
            calendarBody: element.querySelector(`.${componentClasses.body}`),
            monthSelect: element.querySelector(`.${componentClasses.monthSelect}`),
            yearSelect: element.querySelector(`.${componentClasses.yearSelect}`),
            todayButton: element.querySelector(`.${componentClasses.today}`),
            confirmButton: element.querySelector(`.${componentClasses.confirm}`),
            cancelButton: element.querySelector(`.${componentClasses.cancel}`),
            headerPolite: element.querySelector(`.${componentClasses.headerPolite}`),
            gridHeadRow: element.querySelector(
                `.${componentClasses.grid.head} .${componentClasses.grid.row}`
            ),
            gridBodyRows: element.querySelectorAll(
                `.${componentClasses.grid.body} .${componentClasses.grid.row}`
            ),
        };
    };

    /**
     * Centralize changes to currentDate updating all dependencies to generate a new calendar view triggering updateCalendar
     * @param {Date} date
     */
    const setCurrentDate = (date) => {
        currentDate = new Date(date);
        updateCalendar();
    };

    /**
     * Centralize changes to focusedDate updating all the slots
     * @param {Date} date
     * @param {Boolean} focusSlot - Force focus to the slot
     */
    const setFocusedDate = (date, focusSlot = false) => {
        focusedDate = new Date(date);
        let newFocusedSlot;

        daySlots.forEach((daySlot) => {
            if (isSameDate(focusedDate, daySlot.date)) {
                newFocusedSlot = daySlot.element;
                renderState(views.day.focusable, daySlot.element);
            } else {
                renderState(views.day.notFocusable, daySlot.element);
            }
        });

        if (focusSlot && newFocusedSlot) newFocusedSlot.focus();
    };

    /**
     * Similar to user-is-tabbing concept, understand and apply a class that indicate user is pressing keys like (Arrows, Home, End...)
     * Works in pair with unsetUserIsPressing
     */
    const setUserIsPressing = () => {
        isPressing = true;
        renderState(views.calendar.isPressing, element);
        window.addEventListener("click", unsetUserIsPressing);
    };

    /**
     * Understand if user is clicking and disable the pressing state
     * Works in pair with setUserIsPressing
     * @param {MouseEvent} e
     */
    const unsetUserIsPressing = (e) => {
        // Could use e.detail, but using screen readers the first time the button is clicked with enter/space the detail comes 1
        // If a button is used with enter or space keys a window click is triggered
        // Only disable the is pressing class if the user has clicked and not enter/space keys
        if (e.offsetX !== 0 && e.offsetY !== 0) {
            renderState(views.calendar.base, element);
            window.removeEventListener("click", unsetUserIsPressing);
            isPressing = false;
        }
    };

    /**
     * Use mousedown to preventDefault click on focusable and inactive dates
     * @param {MouseEvent} e
     */
    const handleDaySlotMousedown = (e) => {
        if (e.target.getAttribute("aria-disabled") === "true") {
            e.preventDefault();
        }
    };

    /**
     * Day slot click handler
     * @param {Object} daySlot
     * @param {MouseEvent} e
     * @returns
     */
    const handleDaySlotClick = (daySlot) => (e) => {
        if (e.target.getAttribute("aria-disabled") === "false") {
            selectDate(daySlot.date);
        }
    };

    /**
     * Day slot keydown handler
     * @param {Object} daySlot
     * @param {MouseEvent} e
     * @returns
     */
    const handleDaySlotKeydown = (daySlot) => (e) => {
        // 13 (enter) and 32 (space) also trigger the isPressing state
        if (!isPressing && [13, 32, 37, 38, 39, 40, 33, 34, 36, 35].includes(e.keyCode)) {
            setUserIsPressing();
        }

        if (e.shiftKey && [33, 34].includes(e.keyCode)) {
            e.preventDefault();
            let year = 0;
            if (e.keyCode === 33) year++;
            if (e.keyCode === 34) year--;
            const newDay = new Date(focusedDate);
            newDay.setFullYear(focusedDate.getFullYear() - year);
            if (
                isDateOutsideSlots(newDay, currentDate, weekStartDayIndex) &&
                isDateAfter(newDay, mergedProps.startDate) &&
                isDateBefore(newDay, mergedProps.endDate)
            ) {
                setCurrentDate(newDay);
                setFocusedDate(newDay, true);
            } else if (
                isDateOutsideSlots(newDay, currentDate, weekStartDayIndex) &&
                isSameDateOrBefore(newDay, mergedProps.startDate)
            ) {
                setCurrentDate(mergedProps.startDate);
                setFocusedDate(mergedProps.startDate);
            } else if (
                isDateOutsideSlots(newDay, currentDate, weekStartDayIndex) &&
                isSameDateOrAfter(newDay, mergedProps.endDate)
            ) {
                setCurrentDate(mergedProps.endDate);
                setFocusedDate(mergedProps.endDate);
            }
        }

        // Page up
        if (e.keyCode === 33 && !e.shiftKey) {
            e.preventDefault();
            if (isDateAfter(daySlot.date, mergedProps.startDate, "month")) handlePrevClick();
        }

        // Page down
        if (e.keyCode === 34 && !e.shiftKey) {
            e.preventDefault();
            if (isDateBefore(daySlot.date, mergedProps.endDate, "month")) handleNextClick();
        }

        if ([35, 36, 37, 38, 39, 40].includes(e.keyCode)) {
            // Prevent scrolling (arrows)
            e.preventDefault();
            let days = 0;

            if (e.keyCode === 35) days = +(6 - focusedDate.getDay()); // End
            if (e.keyCode === 36) days = -focusedDate.getDay(); // Home
            if (e.keyCode === 37) days = -1; // Arrow left
            if (e.keyCode === 38) days = -7; // Arrow up
            if (e.keyCode === 39) days = 1; // Arrow right
            if (e.keyCode === 40) days = 7; // Arrow down

            const newDay = new Date(focusedDate);
            newDay.setDate(focusedDate.getDate() + days);

            if (
                isDateOutsideSlots(newDay, currentDate, weekStartDayIndex) &&
                isDateAfter(newDay, mergedProps.startDate) &&
                isDateBefore(newDay, mergedProps.endDate)
            ) {
                setCurrentDate(newDay);
                setFocusedDate(newDay, true);
            } else if (!isDateOutsideSlots(newDay, currentDate, weekStartDayIndex)) {
                setFocusedDate(newDay, true);
            }
        }

        if (e.keyCode === 13 || e.keyCode === 32) {
            // Prevent submission (arrows)
            e.preventDefault();
            if (!inactiveDates.has(getDateHash(daySlot.date))) selectDate(daySlot.date); // Enter
        }
    };

    /**
     * Month selection change handler
     * @param {Event} e
     */
    const handleMonthChange = (e) => {
        setCurrentDate(new Date(currentDate).setMonth(e.target.value));
        setFocusedDate(new Date(focusedDate).setMonth(e.target.value));
    };

    /**
     * Year selection change handler
     * @param {Event} e
     */
    const handleYearChange = (e) => {
        const newYear = new Date(currentDate);
        newYear.setFullYear(e.target.value);

        if (isDateBefore(newYear, mergedProps.startDate)) {
            setCurrentDate(mergedProps.startDate);
            setFocusedDate(mergedProps.startDate);
        } else if (isDateAfter(newYear, mergedProps.endDate)) {
            setCurrentDate(mergedProps.endDate);
            setFocusedDate(mergedProps.endDate);
        } else {
            setCurrentDate(new Date(currentDate).setFullYear(e.target.value));
            setFocusedDate(new Date(focusedDate).setFullYear(e.target.value));
        }
    };

    /**
     * Prev month click handler
     */
    const handlePrevClick = () => {
        // Place before setCurrentDate so currentDate didn't changed yet
        const newFocusedDate = new Date(currentDate);
        newFocusedDate.setMonth(currentDate.getMonth() - 1);

        setCurrentDate(new Date(currentDate).setMonth(currentDate.getMonth() - 1));
        setFocusedDate(newFocusedDate);
    };

    /**
     * Next month click handler
     */
    const handleNextClick = () => {
        // Place before setCurrentDate so currentDate didn't changed yet
        const newFocusedDate = new Date(currentDate);
        newFocusedDate.setMonth(currentDate.getMonth() + 1);

        setCurrentDate(new Date(currentDate).setMonth(currentDate.getMonth() + 1));
        setFocusedDate(newFocusedDate);
    };

    /**
     * Today click handler
     */
    const handleTodayButtonClick = () => {
        selectDate(new Date());
    };

    /**
     * Confirm click handler
     */
    const handleConfirmClick = () => {
        selectDate(focusedDate);
        mergedProps.onConfirmClick(selectedDate);
    };

    /**
     * Cancel click handler
     */
    const handleCancelClick = () => {
        mergedProps.onCancelClick();
    };

    // Methods

    /**
     * Clear selectedDate focusing new available date
     */
    const clearSelectedDate = () => {
        selectedDate = null;

        const availableDate = getAvailableViewDate(new Date());

        setCurrentDate(availableDate);
        setFocusedDate(availableDate);

        mergedProps.onSelectDate(null);
        dispatchEvent(eventMap.selectDate, element, {
            formattedDate: null,
            date: null,
        });
    };

    /**
     * Get the focused slot element
     * @returns {HTMLElement}
     */
    const getFocusedSlot = () => {
        return calendarBody.querySelector('[tabindex="0"]');
    };

    /**
     * Select date
     * @param {Date} date
     */
    const selectDate = (date) => {
        if (
            !isValidDate(
                date,
                mergedProps.startDate,
                mergedProps.endDate,
                mergedProps.inactiveDates
            )
        ) {
            console.warn(`${componentName} invalid date`);
            return;
        }

        selectedDate = new Date(date);

        setCurrentDate(selectedDate);
        setFocusedDate(selectedDate, true);

        mergedProps.onSelectDate(selectedDate);
        dispatchEvent(eventMap.selectDate, element, {
            formattedDate: formatDate(date, mergedProps.format),
            date,
        });
    };

    /**
     * Set startDate prop
     * @param {Date} startDate
     * @returns {Void|Boolean}
     */
    const setStartDate = (startDate) => {
        const [validateSuccess] = validateProps({ startDate }, { startDate: schema.startDate });

        if (!validateSuccess) {
            console.warn(`${componentName} startDate is invalid`);
            return false;
        }

        if (!isDateBefore(startDate, mergedProps.endDate)) {
            console.warn(`${componentName} startDate must be a date before as endDate`);
            return false;
        }

        if (selectedDate && !isSameDateOrBefore(startDate, selectedDate)) {
            console.warn(`${componentName} startDate must be a date same or before as selectDate`);
            return false;
        }

        mergedProps.startDate = startDate;
        const availableViewDate = getAvailableViewDate(selectedDate);
        setCurrentDate(availableViewDate);
    };

    /**
     * Set endDate prop
     * @param {Date} endDate
     * @returns {Void|Boolean}
     */
    const setEndDate = (endDate) => {
        const [validateSuccess] = validateProps({ endDate }, { endDate: schema.endDate });

        if (!validateSuccess) {
            console.warn(`${componentName} endDate is invalid`);
            return false;
        }

        if (!isDateAfter(endDate, mergedProps.startDate)) {
            console.warn(`${componentName} endDate must be a date after as startDate`);
            return false;
        }

        if (selectedDate && !isSameDateOrAfter(endDate, selectedDate)) {
            console.warn(`${componentName} endDate must be a date same or after as selectDate`);
            return false;
        }

        mergedProps.endDate = endDate;
        const availableViewDate = getAvailableViewDate(selectedDate);
        setCurrentDate(availableViewDate);
    };

    /**
     * Clean all the listeners and the variable in the element
     */
    const dispose = () => {
        daySlots.forEach((daySlot) => {
            daySlot.element.removeEventListener("click", handleDaySlotClick);
            daySlot.element.removeEventListener("mousedown", handleDaySlotMousedown);
            daySlot.element.removeEventListener("keydown", handleDaySlotKeydown);
        });

        monthSelect.removeEventListener("change", handleMonthChange);
        yearSelect.removeEventListener("change", handleYearChange);
        prevButton.removeEventListener("click", handlePrevClick);
        nextButton.removeEventListener("click", handleNextClick);
        todayButton.removeEventListener("click", handleTodayButtonClick);
        confirmButton.removeEventListener("click", handleConfirmClick);
        cancelButton.removeEventListener("click", handleCancelClick);

        element[componentName] = null;
        element = null;
    };

    // States
    // States created in the initialization of the component and changed once update is called
    let selectedDate = mergedProps.selectedDate ? new Date(mergedProps.selectedDate) : null;
    // Variables focusedDate and currentDate are keep apart from each other
    // currentDate is used to render the calendar (The day is not used here only the month to render current month calendar)
    // focusedDate is the focusable slot day (tabindex="0") we keep it apart from currentDate because it can be outside of the month range (out month dates)
    let focusedDate = getAvailableViewDate(mergedProps.selectedDate);
    let currentDate = new Date(focusedDate);
    const inactiveDates = getInactiveDatesMap(mergedProps.inactiveDates);

    // Variable weekStartDayIndex is used to build weekdays and also the order of all slots
    const weekStartDayIndex = getWeekStartDayIndex(mergedProps.weekStart);
    let isPressing = false;

    // Build calendar structure and give access to elements
    buildStructure();

    // After buildStructure() we have access to all elements
    const {
        todayButton,
        prevButton,
        nextButton,
        calendarBody,
        monthSelect,
        yearSelect,
        confirmButton,
        cancelButton,
        gridHeadRow,
        gridBodyRows,
        headerPolite,
    } = getElements();

    // Depends on getElements();
    buildWeekDays();
    const daySlots = buildDaysSlots();
    updateCalendar();
    setFocusedDate(focusedDate);

    monthSelect.addEventListener("change", handleMonthChange);
    yearSelect.addEventListener("change", handleYearChange);
    prevButton.addEventListener("click", handlePrevClick);
    nextButton.addEventListener("click", handleNextClick);
    todayButton.addEventListener("click", handleTodayButtonClick);
    confirmButton.addEventListener("click", handleConfirmClick);
    cancelButton.addEventListener("click", handleCancelClick);

    const api = {
        clearSelectedDate,
        dispose,
        element,
        getFocusedSlot,
        selectDate,
        setEndDate,
        setStartDate,
    };

    element[componentName] = api;

    return api;
};

export default Calendar;
